/**
 * Portal Context
 *
 * Manages active portal state for the current user session.
 * Loads portals accessible by the user's role, provides:
 *   - List of available portals
 *   - Currently active portal + its navigation, pages, translations
 *   - Dynamic route and sidenav integration
 *   - Widget editability control
 *   - Per-portal language override
 */

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useAuth } from "context/AuthContext";
import { useAbility } from "context/AbilityContext";
import usePortals from "hooks/usePortals";
import logger from "services/logger";

const PortalContext = createContext(null);
PortalContext.displayName = "PortalContext";

// ==================== ACTION TYPES ====================
const ACTION_TYPES = {
  SET_PORTALS: "SET_PORTALS",
  SET_ACTIVE_PORTAL: "SET_ACTIVE_PORTAL",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  SET_TRANSLATIONS: "SET_TRANSLATIONS",
  SET_USER_WIDGET_OVERRIDES: "SET_USER_WIDGET_OVERRIDES",
  CLEAR: "CLEAR",
};

// ==================== INITIAL STATE ====================
const initialState = {
  portals: [],
  activePortal: null,
  activePortalId: null,
  loading: true,
  error: null,
  translations: {},
  userWidgetOverrides: {},
};

// ==================== REDUCER ====================
function portalReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.SET_PORTALS:
      return { ...state, portals: action.payload, loading: false };

    case ACTION_TYPES.SET_ACTIVE_PORTAL:
      return {
        ...state,
        activePortal: action.payload,
        activePortalId: action.payload?.id || null,
        translations: action.payload?.translations || {},
      };

    case ACTION_TYPES.SET_LOADING:
      return { ...state, loading: action.payload };

    case ACTION_TYPES.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case ACTION_TYPES.SET_TRANSLATIONS:
      return { ...state, translations: action.payload };

    case ACTION_TYPES.SET_USER_WIDGET_OVERRIDES:
      return { ...state, userWidgetOverrides: action.payload };

    case ACTION_TYPES.CLEAR:
      return initialState;

    default:
      return state;
  }
}

// ==================== HELPERS ====================

/**
 * Check if a widget is user-editable in the current portal context.
 * Default is false (not editable) unless the portal page config says otherwise.
 */
function isWidgetEditable(page, widgetKey) {
  if (!page || !page.widgets || !page.widgets[widgetKey]) return false;
  return page.widgets[widgetKey].userEditable === true;
}

/**
 * Get the resolved widget config for a widget on a page.
 * Merges registry config with portal-level overrides.
 */
function getWidgetConfig(page, widgetKey) {
  if (!page || !page.widgets) return null;
  return page.widgets[widgetKey] || null;
}

/**
 * Resolve a translatable string from widget config.
 * If the value is an object with language keys, picks the right one.
 * Otherwise returns the string as-is.
 */
function resolveTranslatableString(value, language = "en", fallbackLanguage = "en") {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value[language] || value[fallbackLanguage] || value[Object.keys(value)[0]] || "";
  }
  return String(value);
}

// ==================== PROVIDER ====================
export function PortalProvider({ children }) {
  const [state, dispatch] = useReducer(portalReducer, initialState);
  const { currentUser, activeRole } = useAuth();
  const ability = useAbility();
  const { getMyPortals, getPortalBySlug } = usePortals();

  // Load portals for the current user
  const loadPortals = useCallback(async () => {
    if (!currentUser || !activeRole) {
      dispatch({ type: ACTION_TYPES.CLEAR });
      return;
    }

    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });

    try {
      const portals = await getMyPortals("en");
      dispatch({ type: ACTION_TYPES.SET_PORTALS, payload: portals });
      logger.debug("[Portal]", `Loaded ${portals.length} portals for role ${activeRole}`);
    } catch (err) {
      logger.warn("[Portal]", "Failed to load portals:", err.message);
      dispatch({ type: ACTION_TYPES.SET_ERROR, payload: err.message });
    }
  }, [currentUser, activeRole, getMyPortals]);

  // Load on auth change
  useEffect(() => {
    loadPortals();
  }, [loadPortals]);

  // Set active portal (by slug or portal object)
  const setActivePortal = useCallback(
    async (portalOrSlug) => {
      if (!portalOrSlug) {
        dispatch({ type: ACTION_TYPES.SET_ACTIVE_PORTAL, payload: null });
        return;
      }

      if (typeof portalOrSlug === "string") {
        // It's a slug — find in loaded portals or fetch
        const found = state.portals.find((p) => p.slug === portalOrSlug);
        if (found) {
          dispatch({ type: ACTION_TYPES.SET_ACTIVE_PORTAL, payload: found });
          return;
        }
        try {
          const portal = await getPortalBySlug(portalOrSlug);
          if (portal) {
            dispatch({ type: ACTION_TYPES.SET_ACTIVE_PORTAL, payload: portal });
          }
        } catch (err) {
          logger.warn("[Portal]", `Failed to load portal by slug "${portalOrSlug}":`, err.message);
        }
      } else {
        dispatch({ type: ACTION_TYPES.SET_ACTIVE_PORTAL, payload: portalOrSlug });
      }
    },
    [state.portals, getPortalBySlug]
  );

  // Check if a feature is enabled in the active portal
  const isFeatureEnabled = useCallback(
    (featureId) => {
      if (!state.activePortal) return false;
      return (state.activePortal.enabledFeatures || []).includes(featureId);
    },
    [state.activePortal]
  );

  // Check if a module is enabled in the active portal
  const isModuleEnabled = useCallback(
    (moduleId) => {
      if (!state.activePortal) return false;
      return (state.activePortal.enabledModules || []).includes(moduleId);
    },
    [state.activePortal]
  );

  // Get navigation items for the active portal
  const getNavigation = useCallback(() => {
    if (!state.activePortal) return [];
    return (state.activePortal.navigation || []).filter((nav) => {
      // Filter by CASL if the nav item specifies a subject/action
      if (nav.caslAction && nav.caslSubject) {
        return ability.can(nav.caslAction, nav.caslSubject);
      }
      return true;
    });
  }, [state.activePortal, ability]);

  // Get a specific page from the active portal
  const getPage = useCallback(
    (pageId) => {
      if (!state.activePortal || !state.activePortal.pages) return null;
      return state.activePortal.pages.find((p) => p.id === pageId) || null;
    },
    [state.activePortal]
  );

  // Get page by slug
  const getPageBySlug = useCallback(
    (pageSlug) => {
      if (!state.activePortal || !state.activePortal.pages) return null;
      return state.activePortal.pages.find((p) => p.slug === pageSlug) || null;
    },
    [state.activePortal]
  );

  // Resolve breadcrumbs using the portal's template
  const getBreadcrumbs = useCallback(
    (pageName) => {
      if (!state.activePortal) return [{ label: pageName }];
      const template = state.activePortal.breadcrumbTemplate || "{portalName} > {pageName}";
      const parts = template.split(" > ").map((part) => {
        return part
          .replace("{portalName}", state.activePortal.name)
          .replace("{pageName}", pageName || "");
      });
      return parts.map((label) => ({ label: label.trim() }));
    },
    [state.activePortal]
  );

  // Get default language for the active portal
  const getDefaultLanguage = useCallback(() => {
    if (!state.activePortal) return "en";
    return state.activePortal.defaultLanguage || "en";
  }, [state.activePortal]);

  // Translate a key using portal translations
  const translatePortal = useCallback(
    (key) => {
      const translations = state.translations || {};
      if (translations[key]) return translations[key];
      // Fall back to key itself
      return key;
    },
    [state.translations]
  );

  // Reload portals
  const reload = useCallback(() => {
    loadPortals();
  }, [loadPortals]);

  const value = useMemo(
    () => ({
      ...state,
      setActivePortal,
      isFeatureEnabled,
      isModuleEnabled,
      getNavigation,
      getPage,
      getPageBySlug,
      getBreadcrumbs,
      getDefaultLanguage,
      translatePortal,
      isWidgetEditable,
      getWidgetConfig,
      resolveTranslatableString,
      reload,
    }),
    [
      state,
      setActivePortal,
      isFeatureEnabled,
      isModuleEnabled,
      getNavigation,
      getPage,
      getPageBySlug,
      getBreadcrumbs,
      getDefaultLanguage,
      translatePortal,
      reload,
    ]
  );

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

// ==================== HOOKS ====================
export function usePortalContext() {
  const context = useContext(PortalContext);
  if (!context) throw new Error("usePortalContext must be used within PortalProvider");
  return context;
}

export function useActivePortal() {
  const { activePortal } = usePortalContext();
  return activePortal;
}

export function usePortalNavigation() {
  const { getNavigation } = usePortalContext();
  return getNavigation();
}

export function usePortalPage(pageId) {
  const { getPage } = usePortalContext();
  return getPage(pageId);
}

export function usePortalTranslation() {
  const { translatePortal, getDefaultLanguage } = usePortalContext();
  return { t: translatePortal, defaultLanguage: getDefaultLanguage() };
}

export default PortalContext;
